import { Net, lerpEnt } from './net';
import { storyOn } from './story-flags';
import { Renderer, VW, VH, HD } from './render';
import { CHAR_WORLD_H } from './character-art';
import { PALETTE } from './theme';
import { Input, Action } from './input';
import { Touch } from './touch';
import { Hud } from './hud';
import { UI, ZOOMS, esc } from './ui';
import { Audio, STATIONS } from './audio';
import { listTargets, DriverAssist, HackTarget, applicable } from './targets';
import { PHYS_DT } from '../../common/src/sim';
import { BOSSES, CONFIG, HackId, WORLD } from '../../common/src/data';
import { TILE, districtAt } from '../../common/src/map';
import { rainPlace } from './weather';
import { maskOf, type Snapshot } from '../../common/src/protocol';
import { CREW, crewOf } from './crew';
import { startBackdrop } from './startscreen';
import { startTitleMusic } from './titlemusic';
import { lerp, dist } from '../../common/src/util';
import { Tutorial } from './tutorial';
import { GoalUI } from './goals';
import { StoryPopup } from './story-popup';
import { weaponSpec, type ThrowKind } from '../../common/src/rules';
import { Feedback } from './feedback';
import { installErrorCapture, openBugForm, fbContext } from './bugreport';
import { ThrowSel, THROW_ICON } from './throwable';

const $ = (id: string) => document.getElementById(id)!;
installErrorCapture();
const net = new Net();
const audio = new Audio();
const R = new Renderer();
const fb = new Feedback();
let input!: Input; let hud!: Hud; let ui!: UI; let touch!: Touch; let tut!: Tutorial; let goals!: GoalUI; let lastGoal: ReturnType<GoalUI['update']> = null;
let touchHackKey: string | null = null;
let lastTapDiag: unknown = null; // test diagnostics for tap-to-select
let hackMode = false, showMap = false, lastErr = '', lastErrT = 0;
const assist = new DriverAssist();
let driverTarget: HackTarget | null = null, hoverTarget: HackTarget | null = null;
let padTargetIdx = 0;
/** the throwable G throws (kept client-side; the server checks the kind and the count). Falls back to one we still carry. */
const throwSel = new ThrowSel();
const activeThrow = (): ThrowKind => (net.char ? throwSel.current(net.char) : 'star');
let lastSnapSeen = 0; let fps = 60, frames = 0, fpsT = 0;
let scale = 1;
let districtId = ''; // the district the camera is in, for the entry message and ambience
let districtSaid = { prev: '', last: '', t: -1e9 }; // the last two districts announced, and when
let override: null | { mx: number; my: number; fire: boolean; ax?: number; ay?: number; hb?: boolean } = null;

// ------------------------------------------------------------------ boot
async function api(path: string, opts: RequestInit = {}) { const r = await fetch(path, opts); if (!r.ok) { const m = await r.json().then(j => j?.error, () => null); throw new Error(typeof m === 'string' ? m : `${path} ${r.status}`); } return r.json(); }
function getToken(): string | null { try { return localStorage.getItem('nk.token'); } catch { return null; } }
function setToken(t: string) { try { localStorage.setItem('nk.token', t); } catch { /* storage blocked: session-only character */ } }

async function ensureCharacter(name?: string) {
  let tok = getToken();
  if (tok) { try { const r = await api('/api/me', { headers: { authorization: `Bearer ${tok}` } }); return { token: tok, character: r.character }; } catch { tok = null; } }
  const r = await api('/api/guest', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name: name || 'Runner' }) });
  setToken(r.token); return r;
}

async function boot() {
  const params = new URLSearchParams(location.search);
  const roomParam = params.get('room')?.toUpperCase() ?? '';
  ($('code') as HTMLInputElement).value = roomParam;
  const existing = getToken();
  if (existing) {
    try { const r = await api('/api/me', { headers: { authorization: `Bearer ${existing}` } }); showCharCard(r.character); } catch { /* new */ }
  }
  showTopRunners();
  showMaskPicker();
  $('create').onclick = () => start(null);
  $('join').onclick = () => start(($('code') as HTMLInputElement).value.trim().toUpperCase());
  if (roomParam) { $('join').textContent = `Join ${roomParam}`; $('start').classList.add('has-room'); }
  $('reportbug').onclick = openBugForm;
  $('showjoin').onclick = () => { $('start').classList.add('joining'); ($('code') as HTMLInputElement).focus(); };
  // Enter does the main thing: play, or join the room from the link; in the code box it joins that code
  $('name').addEventListener('keydown', e => { if (e.key === 'Enter') ($(roomParam ? 'join' : 'create') as HTMLButtonElement).click(); });
  $('code').addEventListener('keydown', e => { if (e.key === 'Enter') $('join').click(); });
  // a suspended or interrupted context (phone locked, a call, the tab in the background) resumes on the next touch
  for (const t of ['pointerup', 'touchend', 'keydown']) addEventListener(t, () => audio.resume(), { passive: true });
  // back from a reload after the GPU dropped the game (see setupGame): rejoin that room without the start page
  const rejoin = (() => { try { const c = sessionStorage.getItem('nk.rejoin'); sessionStorage.removeItem('nk.rejoin'); return c; } catch { return null; } })();
  stopBackdrop = startBackdrop($('bg') as HTMLCanvasElement);
  const stopMusic = startTitleMusic(), stopCity = stopBackdrop;
  stopBackdrop = () => { stopCity(); stopMusic(); };
  if (rejoin && existing && rejoin === roomParam) void start(rejoin); // a failed rejoin leaves the normal start page
}
let stopBackdrop = () => {};
async function showTopRunners() {
  try {
    const { top } = await api('/api/leaderboard') as { top: { name: string; earned: number; money: number; level: number }[] };
    if (!top.length) return;
    const $n = (v: number) => '$' + Math.round(v).toLocaleString();
    $('top').innerHTML = `<h3>TOP RUNNERS · TOP 6</h3><table><tr><th>#</th><th>Runner</th><th>Lv</th><th>Total earned</th><th>Cash now</th></tr>`
      + top.slice(0, 6).map((r, i) => `<tr><td>${i + 1}</td><td>${esc(r.name)}</td><td>${r.level}</td><td>${$n(r.earned)}</td><td>${$n(r.money)}</td></tr>`).join('') + '</table>';
  } catch { /* the board is optional */ }
}
function showCharCard(c: any) {
  fbContext.charId = String(c.id ?? ''); fbContext.charName = String(c.name ?? '');
  ($('name') as HTMLInputElement).value = c.name;
}

/** Join-screen mask choice: remembered per browser, sent in the socket handshake, shown to the whole crew. */
let pickedMask = (() => { try { return maskOf(Number(localStorage.getItem('nk.mask') ?? 0)); } catch { return 0; } })();
if (pickedMask < 0) pickedMask = 0;
function showMaskPicker() {
  const row = $('masks'); row.innerHTML = '';
  CREW.forEach((m, i) => {
    const b = document.createElement('button'); b.className = 'mask'; b.title = m.name; b.dataset.mask = String(i);
    b.style.setProperty('--mc', m.color); b.setAttribute('aria-pressed', 'false');
    b.setAttribute('aria-label', `Choose ${m.name}`);
    b.innerHTML = `<canvas aria-hidden="true"></canvas><span>${esc(m.name)}</span>`;
    b.onclick = () => {
      pickedMask = i; try { localStorage.setItem('nk.mask', String(i)); } catch { /* storage blocked: this visit only */ }
      row.querySelectorAll('button').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      $('start').classList.add('crew-selected');
      $('chosen-crew').textContent = m.name;
      ($('name') as HTMLInputElement).focus();
    };
    row.appendChild(b);
  });
}

async function start(code: string | null) {
  audio.unlock(); // initial user interaction unlocks audio
  $('status').textContent = 'Connecting…';
  try {
    const { token } = await ensureCharacter(($('name') as HTMLInputElement).value.trim());
    if (!code) code = (await api('/api/rooms', { method: 'POST' })).code as string;
    history.replaceState(null, '', `?room=${code}${storyOn() ? '&story=1' : ''}`);
    await setupGame();
    await net.connect(token, code, pickedMask);
    const nm = ($('name') as HTMLInputElement).value.trim();
    if (nm && net.char && nm !== net.char.name) net.act({ t: 'appearance', ap: [net.char.appearance.skin, net.char.appearance.jacket, net.char.appearance.hair, net.char.appearance.portrait], name: nm });
    ui.code = code; ui.you = net.welcome!.you; fbContext.room = code; if (net.char) { fbContext.charId = net.char.id; fbContext.charName = net.char.name; }
    $('start').style.display = 'none'; $('hudwrap').style.display = 'block'; stopBackdrop();
    if (storyOn()) { // story mode teaches through its beats: no tutorial, no goal card; a character with progress is asked once
      if (net.char?.story?.done.length) new StoryPopup(() => net.act({ t: 'story', op: 'reset' })).show();
    }
    else if (!net.welcome!.testHooks && !Tutorial.seen()) tut.start(); // tests start it explicitly through __nk
    else if (!net.welcome!.testHooks) goals.show(); // finished the tutorial earlier: the goal card, once
    ui.pager({ text: `Joined room ${code}. Share ${location.origin}/?room=${code} — ${matchMedia('(pointer: coarse)').matches ? 'tap MENU for menus, HACK to hack, USE to steal a car' : 'Tab for menus, Q to hack, E to steal a car'}.`, kind: 'sys' });
  } catch (e) { $('status').textContent = `Could not join: ${(e as Error).message}`; }
}

let setupDone = false;
async function setupGame() {
  if (setupDone) return; setupDone = true;
  const wrap = $('wrap');
  await R.init(wrap, net.map);
  const game = R.app.canvas as HTMLCanvasElement;
  // iPhones drop the WebGL context when the phone locks or another app takes the GPU, and the painted city never comes
  // back (a frozen or black world). Reload straight back into the room: reconnecting takes over the same player record.
  let glLost = false;
  const reloadIntoRoom = () => {
    const ask = () => ui.pager({ text: 'Your device dropped the game\'s graphics. Reload the page to get them back.', kind: 'warn' });
    try { // at most one reload per 30 s, so a device that keeps losing the GPU doesn't reload forever
      if (Date.now() - Number(sessionStorage.getItem('nk.rejoinAt') ?? 0) < 30_000) return ask();
      sessionStorage.setItem('nk.rejoinAt', String(Date.now())); sessionStorage.setItem('nk.rejoin', ui.code);
    } catch { return ask(); } // storage blocked: a reload would land on the start page
    location.reload();
  };
  game.addEventListener('webglcontextlost', e => { e.preventDefault(); glLost = true; if (document.visibilityState === 'visible') reloadIntoRoom(); });
  document.addEventListener('visibilitychange', () => { if (glLost && document.visibilityState === 'visible') reloadIntoRoom(); });
  const hc = $('hud') as HTMLCanvasElement;
  const layout = () => {
    const W = innerWidth, H = innerHeight;
    R.fitAspect(W, H);
    const k = Math.min(W / VW, H / VH);
    scale = k >= 2 ? Math.floor(k) : k; // integer scaling at 2x and above; fill small (phone) screens fractionally
    const w = VW * scale, h = VH * scale;
    for (const c of [game, hc]) { c.style.width = `${w}px`; c.style.height = `${h}px`; c.style.left = `${(W - w) / 2}px`; c.style.top = `${(H - h) / 2}px`; }
    // the full-resolution HUD canvas is redrawn every frame; low effects caps it (phones are often 2.5-3x)
    const dpr = Math.min(devicePixelRatio || 1, R.lowFx ? 1.25 : 4); hc.width = Math.round(w * dpr); hc.height = Math.round(h * dpr);
    const pn = $('panel'); pn.style.left = `${(W - w) / 2 + 40}px`; pn.style.top = `${(H - h) / 2 + 30}px`; pn.style.width = `${w - 80}px`; pn.style.maxHeight = `${h - 60}px`; pn.dataset.maxh = String(h - 60);
  };
  addEventListener('resize', layout); layout();
  input = new Input(hc, (cx, cy) => { const r = hc.getBoundingClientRect(); return [(cx - r.left) / r.width * VW, (cy - r.top) / r.height * VH]; });
  hud = new Hud(hc, net.map);
  touch = new Touch(hc, (cx, cy) => { const r = hc.getBoundingClientRect(); return [(cx - r.left) / r.width * VW, (cy - r.top) / r.height * VH]; });
  ui = new UI(a => net.act(a));
  ui.onSettings = () => { if (R.lowFx !== ui.settings.lowFx) { R.lowFx = ui.settings.lowFx; layout(); } R.crt = ui.settings.crt; R.setLighting(ui.settings.light); R.setNeon(ui.settings.neon);R.userZoom = ZOOMS.some(z => z.v === ui.settings.zoom) ? ui.settings.zoom : 1; audio.volume = ui.settings.volume; audio.radioVolume = ui.settings.radio; audio.sfxVolume = ui.settings.sfx; audio.muted = ui.settings.mute; audio.radioMuted = ui.settings.radioMute; audio.applyVolumes(); };
  ui.onSettings();
  tut = new Tutorial(ui);
  goals = new GoalUI(); tut.onDone = () => goals.show();
  ui.onReportBug = openBugForm;
  ui.onReplayTutorial = () => { if (ui.open) ui.toggle(); tut.start(); };
  net.onMsg = m => {
    ui.pager(m);
    if (m.kind === 'reward') audio.play('reward'); else if (m.kind === 'warn') { audio.play('alert', 0.6); if (!(R.hackFxOn && m.rc)) { lastErr = m.text; lastErrT = performance.now(); } R.hackRefuse(m.rc); }
  };
  net.onChar = c => { ui.char = c; ui.render(true); ui.afterRender(); };
  net.onUi = u => { ui.toggle(u.open === 'jobs' ? 'jobs' : 'vendor', u.poi); ui.afterRender(); };
  net.onKicked = why => { ui.pager({ text: `Disconnected: ${why}`, kind: 'warn' }); };
  const chat = ui.chat;
  chat.addEventListener('keydown', e => {
    e.stopPropagation();
    if (e.key === 'Enter') { const t = chat.value.trim(); if (t) net.act({ t: 'chat', text: t }); chat.value = ''; chat.blur(); }
    if (e.key === 'Escape') { chat.value = ''; chat.blur(); }
  });
  chat.addEventListener('focus', () => { input.typing = true; input.keys.clear(); });
  chat.addEventListener('blur', () => { input.typing = false; chat.classList.remove('open'); });
  // test/automation hooks (always present; they only drive this client's normal inputs)
  (window as any).__nk = {
    renderer: R, // dev: lighting and art inspection (scripts, screenshots)
    net, get stats() { return net.stats; }, act: (a: any) => net.act(a),
    setOverride: (o: typeof override) => { override = o; }, snapshot: () => net.latest, me: () => net.me(),
    predicted: () => ({ ...net.foot, veh: net.veh }), setLag: (up: number, down: number, jitter: number) => net.setLag(up, down, jitter),
    driverTarget: () => driverTarget, pressQ: () => doAction('hack'),
    /** draw one effect locally (screenshot scripts: a headless frame rate is too low to catch a 0.2 s slash) */
    showEvent: (k: string, x: number, y: number, a?: number, w?: string, col?: string) => R.event(k, x, y, w, a, col, net.me()?.id),
    throwKind: () => activeThrow(),
    /** world px -> client (CSS) px, for driving touch tests */
    toClient: (x: number, y: number) => { const [vx, vy] = R.worldToView(x, y); const r = (R.app.canvas as HTMLCanvasElement).getBoundingClientRect(); return [r.left + vx / VW * r.width, r.top + vy / VH * r.height]; },
    tutorial: { start: () => tut.start(), step: () => tut.step, finish: () => tut.finish() },
  goal: () => (lastGoal ? { id: lastGoal.id, text: lastGoal.text, hasTarget: !!lastGoal.target } : null),
    touchActive: () => touch.active, hackTarget: () => hoverTarget?.key ?? null, touchDebug: () => ({ ...touch.debug(), hackMode, lastTap: lastTapDiag }),
  };
  let last = performance.now(), acc = 0;
  const loop = (now: number) => {
    const elapsed = (now - last) / 1000; last = now;
    const dt = Math.min(0.1, elapsed); // effects and rendering step at most 0.1 s per frame
    acc += Math.min(20 * PHYS_DT, elapsed);
    frames++; fpsT += elapsed; if (fpsT >= 1) { fps = frames; frames = 0; fpsT = 0; }
    input.pollGamepad(ui.open, hackMode, net.isDriver());
    let steps = 0;
    // run every elapsed 60 Hz step (up to 20 per frame, i.e. down to 3 fps) so slow devices still move at full speed
    while (acc >= PHYS_DT && steps < 20) { fixedStep(); acc -= PHYS_DT; steps++; }
    if (steps >= 20) acc = 0;
    for (const a of input.take()) doAction(a);
    for (const a of touch.take()) doAction(a);
    for (const text of touch.takeSaid()) net.act({ t: 'chat', text });
    render(dt, acc / PHYS_DT);
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);
}

let touchAim = 0; // last touch aim angle (kept when the right thumb lifts)
let selfPos = { x: 0, y: 0 }; // where the local player is drawn (predicted, or the vehicle when riding)
function aimWorld(): [number, number] {
  const me = net.me();
  if (touch.active && me) {
    if (touch.aim.on) touchAim = Math.atan2(touch.aim.y, touch.aim.x);
    // light aim assist (client-side input shaping only): snap to the closest hostile inside a narrow cone
    let a = touchAim, best = 0.35;
    for (const n of net.latest?.n ?? []) {
      if (n.st !== 2 || n.k === 'ped') continue;
      const d = Math.hypot(n.x - selfPos.x, n.y - selfPos.y); if (d > 220) continue;
      const da = Math.abs(Math.atan2(Math.sin(Math.atan2(n.y - selfPos.y, n.x - selfPos.x) - touchAim), Math.cos(Math.atan2(n.y - selfPos.y, n.x - selfPos.x) - touchAim)));
      if (da < best) { best = da; a = Math.atan2(n.y - selfPos.y, n.x - selfPos.x); }
    }
    return [selfPos.x + Math.cos(a) * 90, selfPos.y + Math.sin(a) * 90];
  }
  if (input.gamepad && me) { const a = input.padAim; const l = Math.hypot(a.x, a.y) || 1; return [selfPos.x + (a.x / l) * 90, selfPos.y + (a.y / l) * 90]; }
  return R.viewToWorld(input.mouseX, input.mouseY);
}
function fixedStep() {
  if (!net.latest) return;
  const typing = input.typing;
  const driving = net.isDriver();
  let ax = typing ? { mx: 0, my: 0 } : driving ? input.driveAxis() : input.axis();
  // touch driving: point the stick where the car should go (shaped into ordinary throttle/steer, see steerToward)
  if (touch.active && !typing) ax = driving && net.veh ? touch.drive(net.veh.a) : { mx: touch.move.x, my: touch.move.y };
  const [wx, wy] = aimWorld();
  const firing = touch.active ? touch.fire : input.fire(driving);
  // shooting leaves quickhack mode: players who pressed Q by accident thought their gun was broken (owner, 2026-10-12)
  if (hackMode && firing && !typing && !ui.open) hackMode = false;
  const hb = driving && !typing && !ui.open && (touch.active ? touch.held('reload') : input.handbrake());
  let cmd = { mx: ax.mx, my: ax.my, ax: wx, ay: wy, fire: !typing && !ui.open && !hackMode && firing, hb };
  if (override) cmd = { mx: override.mx, my: override.my, fire: override.fire, ax: override.ax ?? wx, ay: override.ay ?? wy, hb: !!override.hb };
  net.step(cmd);
}

function doAction(a: Action) {
  const me = net.me(); if (!me) return;
  const s = net.latest!;
  switch (a) {
    case 'inventory': if (ui.open && touch.active) ui.toggle(); else ui.toggle('inventory'); ui.afterRender(); break; // on touch, MENU also closes
    case 'map': showMap = !showMap; break;
    case 'chat': touch.quickChat(false); ui.chat.classList.add('open'); ui.chat.focus(); break;
    case 'quickchat': touch.quickChat(); break;
    case 'zoom': {
      const i = ZOOMS.findIndex(z => z.v === ui.settings.zoom);
      const next = ZOOMS[(i + 1) % ZOOMS.length]; ui.settings.zoom = next.v; ui.saveSettings(); ui.render(true);
      ui.pager({ text: `Camera: ${next.label}`, kind: 'sys' }); break;
    }
    // Esc backs out of whatever is open (menu, quickhack, map); with nothing open it opens the menu on Settings (owner, 2026-10-12)
    case 'escape': if (ui.open) ui.toggle(); else if (hackMode || showMap) { hackMode = false; showMap = false; } else { ui.toggle('settings'); ui.afterRender(); } break;
    case 'menuUp': case 'menuLeft': if (ui.open) ui.focusMove(-1); else if (hackMode) padTargetIdx--; break;
    case 'menuDown': case 'menuRight': if (ui.open) ui.focusMove(1); else if (hackMode) padTargetIdx++; break;
    case 'menuOk': ui.activate(); break;
    case 'menuBack': ui.toggle(); break;
    case 'tabPrev': ui.cycleTab(-1); ui.afterRender(); break;
    case 'tabNext': ui.cycleTab(1); ui.afterRender(); break;
    case 'interact': net.act({ t: 'interact' }); break;
    case 'reload': net.act({ t: 'reload' }); break;
    case 'ability': net.act({ t: 'ability' }); break;
    case 'throw': if (!me.v && !ui.open) net.act({ t: 'throw', kind: throwSel.current(net.char!) }); break;
    case 'cycleThrow': if (!me.v && !ui.open) { throwSel.cycle(net.char!); ui.pager({ text: `Throwable: ${throwSel.info(net.char!).name}`, kind: 'sys' }); } break;
    case 'radio': if (me.v) net.act({ t: 'radio' }); break;
    case 'respawn': if (me.d) net.act({ t: 'respawn' }); break;
    case 'slot0': net.act({ t: 'weapon', slot: 0 }); break;
    case 'slot1': net.act({ t: 'weapon', slot: 1 }); break;
    case 'slot2': net.act({ t: 'weapon', slot: 2 }); break;
    case 'slot3': net.act({ t: 'weapon', slot: 3 }); break;
    case 'nextWeapon': {
      const c = net.char!; const cur = ['fists', ...c.equipped].findIndex((u, i) => i === 0 ? me.w === 'fists' : c.inventory.find(x => x.uid === u)?.w === me.w && u);
      for (let k = 1; k <= 4; k++) { const sl = ((cur < 0 ? 0 : cur) + k) % 4; if (sl === 0 || c.equipped[sl - 1]) { net.act({ t: 'weapon', slot: sl }); break; } }
      break;
    }
    case 'ping': { const [x, y] = aimWorld(); net.act({ t: 'ping', x, y }); break; }
    case 'hack':
      if (net.isDriver()) {
        if (driverTarget) { const h = applicable(driverTarget)[0]; if (h) sendHack(h, driverTarget, driverTarget.x + Math.cos(net.veh?.a ?? 0) * 120, driverTarget.y + Math.sin(net.veh?.a ?? 0) * 120); }
      } else hackMode = !hackMode;
      break;
    case 'hack1': case 'hack2': case 'hack3': {
      // 1-3 are the weapon slots until quickhack mode (Q) is on, then they pick the hack
      if (!hackMode) { net.act({ t: 'weapon', slot: Number(a.slice(4)) }); break; }
      if (!hoverTarget) break;
      const h = (['short_circuit', 'sensor_jam', 'remote_override'] as HackId[])[Number(a.slice(4)) - 1];
      const [x, y] = aimWorld();
      sendHack(h, hoverTarget, x, y);
      break;
    }
  }
  void s;
}
/** One request to the server. The link drawn here is only an acknowledgement of the key press (and only when our own
 *  state says it could succeed); the server alone decides, and its event or refusal draws the outcome. */
function sendHack(h: HackId, t: HackTarget, ax: number, ay: number) {
  const me = net.me(), s = net.latest;
  net.act({ t: 'hack', hack: h, target: t.key, ax, ay });
  if (!me || !s) return;
  const cfg = CONFIG.hacks[h], from = net.isDriver() && net.veh ? net.veh : me.v ? me : net.foot;
  const ok = Math.hypot(t.x - from.x, t.y - from.y) <= cfg.range && me.ram >= cfg.ram && !(me.hk[h] > 0) && !(s.inf.res[t.key] > 0);
  R.hackAck(from, h, t, ok);
}

// ------------------------------------------------------------------ render
const seenEvents = new WeakSet<Snapshot>();
let radioKey = ''; // last vehicle/station we announced
let sanctSaid = false; // the join-time Shingan line was checked

/** The HUD objective for this player's active challenge, or null. Heat challenges point at the player themselves. */
function objectiveFor(s: Snapshot, myId: number, fx: number, fy: number): { text: string; x: number; y: number; boss?: boolean } | null {
  const ch = s.c.find(c => c.parts.includes(myId)); if (!ch) return null;
  const text = ch.h ? ch.h : ch.k === 'courier' ? (ch.stage === 'pickup' ? 'Pick up the package' : ch.carrier === myId ? 'Deliver the package!' : 'Escort the carrier to the drop') :
    ch.k === 'theft' ? (ch.stage === 'steal' ? 'Steal the marked vehicle' : 'Deliver it to Rust Lotus in usable condition') :
    ch.k === 'boss' ? `${ch.stage === 'incoming' ? 'Get ready:' : 'Take down'} ${BOSSES.tiers[ch.bk!].name}` :
      ch.stage === 'raise' ? `Reach heat level ${ch.stars}` : 'Evade the pursuit (break line of sight)';
  return ch.k === 'heat' ? { text, x: fx, y: fy } : { text, x: ch.tx, y: ch.ty, boss: ch.k === 'boss' };
}

function render(dt: number, alpha: number) {
  const s = net.latest; if (!s) return;
  const me = net.me(); if (!me) return;
  // joining while Shingan is already at the Sanctuary: the same line the server sends when he appears
  if (!sanctSaid) { sanctSaid = true; if (s.sg) ui.pager({ text: `${s.sg.n} is meditating at the Sanctuary.`, kind: 'warn', col: PALETTE.offWhite }); }
  const { a, b, t } = net.interp();
  const base = a ?? s;
  let players = lerpEnt(base.p, b?.p, t);
  let vehicles = lerpEnt(base.v, b?.v, t);
  const npcs = lerpEnt(base.n, b?.n, t);
  const bosses = lerpEnt(base.b ?? [], b?.b, t);
  const decoys = lerpEnt(base.dc ?? [], b ? b.dc ?? [] : undefined, t);
  // self: predicted
  let fx: number, fy: number, speed = 0;
  const myV = me.v ? s.v.find(v => v.id === me.v) : undefined;
  if (net.veh && me.s === 0 && myV) {
    const pv = net.prevVeh ?? net.veh;
    fx = lerp(pv.x, net.veh.x, alpha) + net.visOff.x; fy = lerp(pv.y, net.veh.y, alpha) + net.visOff.y;
    const va = pv.a + ((net.veh.a - pv.a + Math.PI * 3) % (Math.PI * 2) - Math.PI) * alpha;
    vehicles = vehicles.filter(v => v.id !== myV.id).concat([{ ...myV, x: fx, y: fy, a: va, vx: net.veh.vx, vy: net.veh.vy }]);
    speed = Math.hypot(net.veh.vx, net.veh.vy);
  } else if (me.v) {
    const iv = vehicles.find(v => v.id === me.v) ?? myV!; fx = iv.x; fy = iv.y; speed = Math.hypot(iv.vx, iv.vy);
  } else {
    fx = lerp(net.prevFoot.x, net.foot.x, alpha) + net.visOff.x; fy = lerp(net.prevFoot.y, net.foot.y, alpha) + net.visOff.y;
  }
  selfPos = { x: fx, y: fy };
  // entering another district: a pager line names it and the city ambience changes
  const here = districtAt(Math.floor(fx / TILE), Math.floor(fy / TILE));
  if (here.id !== districtId) {
    // announce every new district, except stepping straight back over the boundary just crossed
    const bounce = here.id === districtSaid.prev && performance.now() - districtSaid.t < 8000;
    if (districtId && here.id !== districtSaid.last && !bounce) {
      ui.pager({ text: `${here.name.toUpperCase()}: ${here.tagline}`, kind: 'sys', col: here.color });
      districtSaid = { prev: districtId, last: here.id, t: performance.now() };
    }
    districtId = here.id; audio.setDistrict(here.id);
  }
  const [awx, awy] = aimWorld();
  players = players.map(p => (p.id === me.id ? { ...me, x: fx, y: fy, a: Math.atan2(awy - fy, awx - fx) } : p));
  // hack targets
  const scanner = !!net.char?.cyber.installed.includes('scanner_optics');
  const targets = hackMode || scanner || (net.isDriver() && myV) ? listTargets(s, net.map, fx, fy) : []; // only needed for these modes
  driverTarget = net.isDriver() && myV ? assist.pick(targets, { ...myV, x: fx, y: fy }, performance.now() / 1000, s.inf.res) : null;
  hoverTarget = null;
  if (hackMode && !net.isDriver()) {
    const tap = touch.takeTap();
    if (tap) { // tap-to-select: nearest target to the tapped world point
      const [tx, ty] = R.viewToWorld(tap[0], tap[1]); let bd = 48 / R.cam.zoom; touchHackKey = null;
      for (const tg of targets) { const d = dist(tx, ty, tg.x, tg.y); if (d < bd) { bd = d; touchHackKey = tg.key; } }
      const near = [...targets].sort((p, q) => dist(tx, ty, p.x, p.y) - dist(tx, ty, q.x, q.y))[0];
      lastTapDiag = { view: tap.map(Math.round), world: [Math.round(tx), Math.round(ty)], cam: [Math.round(R.cam.x), Math.round(R.cam.y), +R.cam.zoom.toFixed(2)], nearest: near ? [near.key, Math.round(dist(tx, ty, near.x, near.y))] : null, picked: touchHackKey, n: targets.length };
    }
    if (touch.active) hoverTarget = targets.find(t => t.key === touchHackKey) ?? null;
    else if (input.gamepad) { if (targets.length) { const sorted = [...targets].sort((p, q) => dist(fx, fy, p.x, p.y) - dist(fx, fy, q.x, q.y)); hoverTarget = sorted[((padTargetIdx % sorted.length) + sorted.length) % sorted.length]; } }
    else { let bd = 28 / R.cam.zoom; for (const tg of targets) { const d = dist(awx, awy, tg.x, tg.y); if (d < bd) { bd = d; hoverTarget = tg; } } }
  }
  fb.step(dt);
  // optic look-ahead (gunsmith zoom): mouse leans by how far the cursor is from the centre, stick/touch by the aim direction
  let lx = 0, ly = 0;
  const held = me.sl > 0 && net.char ? net.char.inventory.find(i => i.uid === net.char!.equipped[me.sl - 1]) ?? null : null;
  const look = held && !net.isDriver() ? weaponSpec(held).look ?? 0 : 0;
  if (look > 0) {
    if (touch.active || input.gamepad) { const a = Math.atan2(awy - fy, awx - fx); lx = Math.cos(a) * look; ly = Math.sin(a) * look; }
    else { const mx = input.mouseX - VW / 2, my = input.mouseY - VH / 2, m = Math.hypot(mx, my) || 1, k = Math.min(1, m / (VH / 2)) * look / m; lx = mx * k; ly = my * k; }
  }
  R.frame(dt, { s, players, vehicles, npcs, you: me.id, focus: { x: fx, y: fy, speed, lx, ly }, scanner, hackMode, highlight: hoverTarget?.key ?? driverTarget?.key ?? null, targets: hackMode || scanner ? targets : driverTarget ? [driverTarget] : [], flash: fb.flash, wanted: me.h > 0 && me.ph > 0, bosses, decoys });
  // events + audio (once per snapshot)
  for (const sn of net.snaps) {
    if (seenEvents.has(sn) || sn.t <= lastSnapSeen - 5000) continue; seenEvents.add(sn);
    for (const e of sn.ev) {
      fb.onEvent(e, me.id); R.weaponDamage(e, sn.p); if (e.k === 'dmg') continue;
      if (e.k === 'hack') R.hackConfirm(e, sn);
      R.event(e.k, e.x, e.y, e.w, e.a, e.by !== undefined ? crewOf(sn.p, e.by).color : undefined, e.by, sn.p.find(p => p.id === e.by));
      const vol = Math.max(0, 1 - dist(e.x, e.y, fx, fy) / 450);
      audio.play(e.k, e.q ? vol * 0.5 : vol, e.w, !!e.q);
    }
  }
  lastSnapSeen = s.t;
  // engine, siren, radio
  audio.setEngine(net.isDriver() && myV ? speed : -1, myV?.t ?? 'beater');
  let siren = 0; for (const v of vehicles) if (v.f & 1) siren = Math.max(siren, 1 - dist(v.x, v.y, fx, fy) / 400);
  audio.setSiren(siren);
  audio.setRain(rainPlace(net.map, fx, fy, !!myV));
  if (myV) {
    audio.setRadio(myV.rad, myV.re, net.clockOffset);
    const key = `${myV.id}:${myV.rad}:${myV.re}`; // announce the station when it changes
    if (key !== radioKey && myV.rad) ui.pager({ text: `📻 ${STATIONS[myV.rad].name} — ${STATIONS[myV.rad].dj}`, kind: 'sys', col: '#9a8cff' });
    radioKey = key;
  } else { audio.setExploration(net.clockOffset); radioKey = ''; }
  const tutObjective = tut.update({ s, me, char: net.char!, x: fx, y: fy, touch: touch.active });
  const objective = objectiveFor(s, me.id, fx, fy) ?? tutObjective;
  const goal = lastGoal = goals.update({ s, id: me.id, char: net.char!, x: fx, y: fy, hidden: !ui.settings.goal || ui.open || tut.active || showMap, inCar: !!me.v, controls: touch.active ? 'touch' : input.gamepad ? 'pad' : 'kb' });
  const storyPin = storyOn() ? goals.story({ s, char: net.char!, x: fx, y: fy, inCar: !!me.v, controls: 'kb' }) : null;
  if (lastErr && performance.now() - lastErrT > 2500) lastErr = '';
  const hc = hud.cv; const S = hc.width / VW;
  hud.draw({
    s, me: { ...me, x: fx, y: fy }, char: net.char!, scale: S * R.cam.zoom, w2s: (x, y) => { const [vx, vy] = R.worldToView(x, y); return [vx * S, vy * S]; },
    mouse: input.gamepad || touch.active ? (() => { const [vx, vy] = R.worldToView(awx, awy); return [vx * S, vy * S] as [number, number]; })() : [input.mouseX * S, input.mouseY * S],
    storyPin: storyPin?.pin ? storyPin.target : null, hackMode, hackTarget: hoverTarget, driverTarget, hackFx: R.hackFxOn, hackLabels: R.hackFxOn ? R.hackFx.labels : [], players, objective, goal, lowFx: R.lowFx, characterHeight: HD ? CHAR_WORLD_H : undefined,
    fps, rtt: Math.round(net.rtt), radio: myV?.rad ?? 0, showMap, scanner, clubBoss: (() => { const cb = s.cb, n = cb && npcs.find(e => e.id === cb.id); return cb && n ? { x: n.x, y: n.y, hp: n.hp, mhp: n.mhp, n: cb.n } : undefined; })(), abbot: (() => { const sg = s.sg, b = sg && npcs.find(e => e.id === sg.id); return sg && b ? npcs.filter(e => e.id === sg.id || sg.dc.includes(e.id)).map(e => ({ x: e.x, y: e.y, hp: b.hp, mhp: b.mhp, n: sg.n, top: R.shinganArtOn ? 52 : 28 })) : undefined; })(), npcsVisible: scanner ? npcs.filter(n => dist(n.x, n.y, fx, fy) < 300) : [], lastErr, touch: touch.active,
    throwable: throwSel.info(net.char!),
  });
  fb.draw(hc.getContext('2d')!, { W: hc.width, H: hc.height, u: Math.max(1, Math.min(hc.width / 640, hc.height / 360)), w2s: (x, y) => { const [vx, vy] = R.worldToView(x, y); return [vx * S, vy * S]; }, t: performance.now() / 1000, me: { ...me, x: fx, y: fy }, myVehicle: me.v, vehicles, lowFx: R.lowFx });
  ui.snap = s; ui.render();
  if (touch.active) {
    if (!hackMode) touch.takeTap(); // taps only matter while targeting
    const canUse = !me.v && (s.v.some(v => Math.hypot(v.x - fx, v.y - fy) < 34) || s.p.some(p => p.d && p.id !== me.id && Math.hypot(p.x - fx, p.y - fy) < 26) || WORLD.pois.some(o => Math.hypot(o.x * TILE - fx, o.y * TILE - fy) < 40));
    touch.update({ driving: net.isDriver(), inVehicle: !!me.v, downed: !!me.d, hackMode, canUse, menuOpen: ui.open || showMap, throwIcon: THROW_ICON[throwSel.current(net.char!)] });
  }
}

void boot();
